import { getHTML, getText } from '@amzn/awscat-editor';
import {
  Content,
  ContentNode,
  Epic,
  Task,
  Troubleshooting,
} from '@amzn/awscat-pattern-library-service-model';
import diff from 'node-htmldiff';
import { deepClone } from '../../api/UtilsApi';
import { imgTagRegex } from './ContentDataUtils';

/************************************** Helper Functions *****************************************/
const hasDiffTagRegex = new RegExp('</ins>|</del>', 'g');

const hasDiffTag = (hasDiff: boolean, toCompareHTMLs: string[]): boolean => {
  return hasDiff || toCompareHTMLs.some((html) => html.match(hasDiffTagRegex));
};

const getDiffHelper = (
  beforeStr: string | object,
  afterStr: string | object,
  diffClass?: string | undefined,
) => {
  const beforeHTML = getHTML(beforeStr);
  const afterHTML = getHTML(afterStr);
  return diff(
    beforeHTML,
    afterHTML,
    beforeHTML.match(imgTagRegex) || afterHTML.match(imgTagRegex) ? 'img-diff' : diffClass,
  );
};

const createBeforeContentNodesMap = (contentNodes: ContentNode[]): Map<string, ContentNode> => {
  // map of section id -> section content node
  return contentNodes.reduce((map, section) => {
    map.set(section.id, section);
    return map;
  }, new Map<string, ContentNode>());
};

const createAllPagesMap = (
  beforeContentNode: ContentNode | undefined,
  pagesMap: Map<string, ContentNode>,
) => {
  if (!beforeContentNode) {
    return pagesMap;
  }

  pagesMap.set(beforeContentNode.id, beforeContentNode);
  beforeContentNode.subContent.forEach((subContent) => {
    pagesMap = createAllPagesMap(subContent, pagesMap);
  });

  return pagesMap;
};

/**************************** Functions for Diffs of Pattern Types *******************************/

/**
 * Get the diff of every story within an epic
 * @param beforeTasks : the stories of the previous content
 * @param afterTasks : the stories of the current content
 * @param hasDiff : whether any story have diff
 * @returns { array of stories with diffs, hasDiff }
 */
const handleStoryChanges = (beforeTasks: Task[], afterTasks: Task[], hasDiff: boolean = false) => {
  // map of previous content's task id -> task object
  const beforeTasksMap = new Map<string, Task>();
  beforeTasks.forEach((task) => {
    beforeTasksMap.set(task.id, task);
  });

  // go through current list of tasks and get diff if have same task previously
  afterTasks.forEach((afterTask, i) => {
    const beforeTask = beforeTasksMap.get(afterTask.id);

    const diffTitle = getDiffHelper(beforeTask?.title ?? '', afterTask.title);
    const diffSkills = getDiffHelper(beforeTask?.skills ?? '', afterTask.skills);
    const diffDescription = getDiffHelper(beforeTask?.description ?? '', afterTask.description);

    afterTask.title = diffTitle;
    afterTask.skills = diffSkills;
    afterTask.description = diffDescription;
    afterTasks[i] = afterTask;

    hasDiff = hasDiffTag(hasDiff, [diffTitle, diffDescription, diffSkills]);
    beforeTasksMap.delete(afterTask.id);
  });

  // if there's tasks left in beforeTasksMap, those are the deleted ones
  if (beforeTasksMap.size > 0) {
    for (const [taskId, beforeTask] of beforeTasksMap) {
      const diffTitle = getDiffHelper(beforeTask.title, '');
      const diffSkills = getDiffHelper(beforeTask.skills, '');
      const diffDescription = getDiffHelper(beforeTask.description, '');

      beforeTask.title = diffTitle;
      beforeTask.skills = diffSkills;
      beforeTask.description = diffDescription;
      afterTasks.push(beforeTask);

      hasDiff = true;
    }
  }

  return { afterTasks, hasDiff };
};

/**
 * Get the diff of every epic in the content
 * @param beforeContentNode : previous epics
 * @param afterContentNode : current epics
 * @param hasDiff : whether any epic have diff
 * @returns { all epics with diffs, hasDiff }
 */
const handleEpicsDiff = (beforeEpics: Epic[], afterEpics: Epic[], hasDiff: boolean = false) => {
  // map of previous content's epic id -> epic
  const beforeEpicsMap = new Map<string, any>();
  beforeEpics.forEach((epic) => {
    beforeEpicsMap.set(epic.id, epic);
  });

  // go through afterContentNode's epics and get diff if have same epic previously
  afterEpics.forEach((afterEpic, i) => {
    const beforeEpic = beforeEpicsMap.get(afterEpic.id);

    const diffTitle = getDiffHelper(beforeEpic?.title ?? '', afterEpic.title);
    const diffDescription = getDiffHelper(beforeEpic?.description ?? '', afterEpic.description);
    const storyChanges = handleStoryChanges(beforeEpic?.tasks ?? [], afterEpic.tasks, hasDiff);

    // @ts-expect-error TS2532 - strict migration
    afterEpics[i].title = diffTitle;
    // @ts-expect-error TS2532 - strict migration
    afterEpics[i].description = diffDescription;
    // @ts-expect-error TS2532 - strict migration
    afterEpics[i].tasks = storyChanges.afterTasks;

    hasDiff = storyChanges.hasDiff || hasDiffTag(hasDiff, [diffTitle, diffDescription]);
    beforeEpicsMap.delete(afterEpic.id);
  });

  // if before epics still have stuff, those are deleted ones
  if (beforeEpicsMap.size > 0) {
    for (const [epicId, beforeEpic] of beforeEpicsMap) {
      const diffTitle = getDiffHelper(beforeEpic.title, '');
      const diffDescription = getDiffHelper(beforeEpic.description, '');
      const storyChanges = handleStoryChanges(beforeEpic.tasks, [], hasDiff);

      beforeEpic.title = diffTitle;
      beforeEpic.description = diffDescription;
      beforeEpic.tasks = storyChanges.afterTasks;
      afterEpics.push(beforeEpic);

      hasDiff = storyChanges.hasDiff || hasDiffTag(hasDiff, [diffTitle, diffDescription]);
    }
  }

  return { afterEpics, hasDiff };
};

const handleTroubleshootingDiff = (
  beforeTroubles: Troubleshooting[],
  afterTroubles: Troubleshooting[],
  hasDiff: boolean = false,
) => {
  // map of troubleshooting id -> trouble
  const beforeTroublesMap = new Map<string, any>();
  beforeTroubles.forEach((trouble) => {
    beforeTroublesMap.set(trouble.id, trouble);
  });
  const diffTroubleshootings = deepClone(afterTroubles);

  // go through afterContentNode's troubleshoots and get diff if have same trouble previously
  afterTroubles.forEach((afterTrouble, i) => {
    // issue and solution are properties came from Tiptap
    // editor. They should be either a string or JSON object.
    // If they are not, this indicates bad data.
    //
    // Ignore for now as this code is being used by the legacy editor
    // and we don't want to throw exception to crash it.
    if (
      (typeof afterTrouble.issue !== 'string' && typeof afterTrouble.issue !== 'object') ||
      (typeof afterTrouble.solution !== 'string' && typeof afterTrouble.solution !== 'object')
    ) {
      return;
    }
    const beforeTrouble = beforeTroublesMap.get(afterTrouble.id);
    // @ts-expect-error TS2345 - strict migration
    const diffIssue = getDiffHelper(beforeTrouble?.issue ?? '', afterTrouble.issue);
    // @ts-expect-error TS2345 - strict migration
    const diffSolution = getDiffHelper(beforeTrouble?.solution ?? '', afterTrouble.solution);

    diffTroubleshootings[i].issue = diffIssue;
    diffTroubleshootings[i].solution = diffSolution;

    hasDiff = hasDiffTag(hasDiff, [diffIssue, diffSolution]);
    beforeTroublesMap.delete(afterTrouble.id);
  });

  // if before troubleshoots still have stuff, those are deleted ones
  if (beforeTroublesMap.size > 0) {
    for (const [troubleId, beforeTrouble] of beforeTroublesMap) {
      const diffIssue = getDiffHelper(beforeTrouble.issue, '');
      const diffSolution = getDiffHelper(beforeTrouble.solution, '');

      beforeTrouble.issue = diffIssue;
      beforeTrouble.solution = diffSolution;
      diffTroubleshootings.push(beforeTrouble);

      hasDiff = hasDiffTag(hasDiff, [diffIssue, diffSolution]);
    }
  }

  return { diffTroubleshootings, hasDiff };
};

const diffContentNodeSinglePage = (
  beforeContentNode: ContentNode | undefined,
  afterContentNode: ContentNode,
  hasDiff: boolean = false,
) => {
  const diffNode: ContentNode = deepClone(afterContentNode);

  if (!beforeContentNode) {
    diffNode.contentData = `<ins>${getHTML(afterContentNode.contentData)}</ins>`;
    return { diffNode, hasDiff: true };
  }
  if (!afterContentNode) {
    diffNode.contentData = `<del>${getHTML(beforeContentNode.contentData)}</del>`;
    return { diffNode, hasDiff: true };
  }

  if (afterContentNode.id === 'epics') {
    const epicsDiff = handleEpicsDiff(
      beforeContentNode.contentData,
      afterContentNode.contentData,
      hasDiff,
    );
    diffNode.contentData = epicsDiff.afterEpics;
    hasDiff = epicsDiff.hasDiff;
    return { diffNode, hasDiff };
  }

  if (afterContentNode.id === 'troubleshooting') {
    const troubleshootingDiff = handleTroubleshootingDiff(
      beforeContentNode.contentData,
      afterContentNode.contentData,
      hasDiff,
    );
    diffNode.contentData = troubleshootingDiff.diffTroubleshootings;
    hasDiff = troubleshootingDiff.hasDiff;
    return { diffNode, hasDiff };
  }

  const node1 = getHTML(beforeContentNode.contentData);
  const node2 = getHTML(afterContentNode.contentData);

  if (typeof node1 === 'string' && typeof node2 === 'string') {
    const contentNodeStringDiff = diff(
      node1,
      node2,
      node1.match(imgTagRegex) || node2.match(imgTagRegex) ? 'img-diff' : undefined,
    );
    diffNode.contentData = contentNodeStringDiff;
    hasDiff = hasDiffTag(hasDiff, [contentNodeStringDiff]);
  }

  for (let i = 0; i < diffNode.subContent.length; i++) {
    const matchNode = beforeContentNode.subContent.find(
      // @ts-expect-error TS2532 - strict migration
      (sc) => sc.id === afterContentNode.subContent[i].id,
    );
    const subContentDiff = diffContentNodeSinglePage(
      matchNode,
      // @ts-expect-error TS2345 - strict migration
      afterContentNode.subContent[i],
      hasDiff,
    );
    diffNode.subContent[i] = subContentDiff.diffNode;
    hasDiff = subContentDiff.hasDiff || hasDiff;
  }

  for (let i = 0; i < diffNode.contentSections.length; i++) {
    const matchNode = beforeContentNode.contentSections.find(
      // @ts-expect-error TS2532 - strict migration
      (cs) => cs.id === afterContentNode.contentSections[i].id,
    );
    const contentSectionDiff = diffContentNodeSinglePage(
      matchNode,
      // @ts-expect-error TS2345 - strict migration
      afterContentNode.contentSections[i],
      hasDiff,
    );
    diffNode.contentSections[i] = contentSectionDiff.diffNode;
    hasDiff = contentSectionDiff.hasDiff || hasDiff;
  }

  return { diffNode, hasDiff };
};

export const diffContentsSinglePage = (beforeContent: Content, afterContent: Content) => {
  const newContent: Content = deepClone(afterContent);
  const { diffNode, hasDiff } = diffContentNodeSinglePage(
    beforeContent.contentData,
    afterContent.contentData,
    false,
  );
  newContent.contentData = diffNode;
  return { newContent, hasDiff };
};

/********************** Functions for Diffs of Guides And Strategy Types *************************/

const handleContentSectionsDiff = (
  beforeSections: ContentNode[],
  afterSections: ContentNode[],
  hasDiff: boolean = false,
) => {
  const beforeSectionsMap = createBeforeContentNodesMap(beforeSections);
  const diffSections = deepClone(afterSections);

  afterSections.forEach((afterSection, i) => {
    const beforeSection = beforeSectionsMap.get(afterSection.id);
    const titleDiff = getDiffHelper(
      getText(beforeSection?.title) ?? '',
      getText(afterSection.title),
    );
    const contentDataDiff = getDiffHelper(
      beforeSection?.contentData ?? '',
      afterSection.contentData,
    );

    diffSections[i].title = titleDiff;
    diffSections[i].contentData = contentDataDiff;

    beforeSectionsMap.delete(afterSection.id);
    hasDiff = hasDiffTag(hasDiff, [titleDiff, contentDataDiff]);
  });

  // remaining sections from before => deleted sections
  beforeSectionsMap.forEach((beforeSection) => {
    beforeSection.title = `<del>${getHTML(beforeSection.title)}<del>`;
    beforeSection.contentData = `<del>${getHTML(beforeSection.contentData)}<del>`;
    diffSections.push(beforeSection);
    hasDiff = true;
  });
  return { diffSections, hasDiff };
};

const diffContentNodeMultiPage = (
  beforeContentNode: ContentNode | undefined,
  afterContentNode: ContentNode | undefined,
  allBeforePages: Map<string, ContentNode>,
  allAfterPages: Map<string, ContentNode>,
  hasDiff: boolean = false,
) => {
  const diffNode: ContentNode = deepClone(afterContentNode ? afterContentNode : beforeContentNode);
  if (!beforeContentNode && !afterContentNode) {
    return { diffNode, hasDiff };
  }

  // get diff of current node
  const beforePage = allBeforePages.get(afterContentNode?.id ?? '');
  const titleDiff = getDiffHelper(
    getText(beforePage?.title) ?? '',
    getText(afterContentNode?.title) ?? '',
    'page-title-diff',
  );
  const contentDataDiff = getDiffHelper(
    beforePage?.contentData ?? '',
    afterContentNode?.contentData ?? '',
  );
  const contentSectionsDiff = handleContentSectionsDiff(
    beforePage?.contentSections ?? [],
    afterContentNode?.contentSections ?? [],
    hasDiff,
  );

  diffNode.title = titleDiff;
  diffNode.contentData = contentDataDiff;
  diffNode.contentSections = contentSectionsDiff.diffSections;
  hasDiff = contentSectionsDiff.hasDiff || hasDiffTag(hasDiff, [titleDiff, contentDataDiff]);

  // get diff of child pages
  const beforeSubContentsMap = createBeforeContentNodesMap(beforeContentNode?.subContent ?? []);
  afterContentNode?.subContent.forEach((afterSubContent, i) => {
    const beforeSubContent = beforeSubContentsMap.get(afterSubContent.id);
    const subContentDiff = diffContentNodeMultiPage(
      beforeSubContent,
      afterSubContent,
      allBeforePages,
      allAfterPages,
      hasDiff,
    );
    diffNode.subContent[i] = subContentDiff.diffNode;
    beforeSubContentsMap.delete(afterSubContent.id);
    hasDiff = hasDiff || subContentDiff.hasDiff;
  });

  // get diff of deleted pages
  if (beforeSubContentsMap.size > 0) {
    for (const [pageId, beforeSubContent] of beforeSubContentsMap) {
      if (allAfterPages.has(pageId)) {
        continue;
      }
      const beforeTitleDiff = getDiffHelper(getText(beforeSubContent.title), '');
      const beforeDataDiff = getDiffHelper(beforeSubContent.contentData, '');
      const beforeSectionsDiff = handleContentSectionsDiff(
        beforeSubContent.contentSections,
        [],
        hasDiff,
      );

      beforeSubContent.title = beforeTitleDiff;
      beforeSubContent.contentData = beforeDataDiff;
      beforeSubContent.contentSections = beforeSectionsDiff.diffSections;
      beforeSubContent.subContent.forEach((subContent, i) => {
        const subContentDiff = diffContentNodeMultiPage(
          subContent,
          undefined,
          allBeforePages,
          allAfterPages,
          hasDiff,
        );
        beforeSubContent.subContent[i] = subContentDiff.diffNode;
        hasDiff = hasDiff || subContentDiff.hasDiff;
      });

      diffNode.subContent.push(beforeSubContent);
      hasDiff =
        hasDiff || hasDiffTag(beforeSectionsDiff.hasDiff, [beforeTitleDiff, beforeDataDiff]);
    }
  }
  return { diffNode, hasDiff };
};

export const diffContentsMultiPage = (beforeContent: Content, afterContent: Content) => {
  const newContent: Content = deepClone(afterContent);
  const beforeClone = deepClone(beforeContent.contentData);
  const afterClone = deepClone(afterContent.contentData);

  // map of page id to page for beforeContent and afterContent
  const allBeforePages = createAllPagesMap(beforeClone, new Map<string, ContentNode>());
  const allAfterPages = createAllPagesMap(afterClone, new Map<string, ContentNode>());

  const { diffNode, hasDiff } = diffContentNodeMultiPage(
    beforeClone,
    afterClone,
    allBeforePages,
    allAfterPages,
    false,
  );
  newContent.contentData = diffNode;
  return { newContent, hasDiff };
};
